import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll, PageHeader, Panel, Pill, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const EMPTY = { name: "", host_patterns: [], max_per_line: 1, max_per_account: 1, notes: "" };

function ProviderForm({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [hosts, setHosts] = useState("");
  useEffect(() => { if (open) { const v = initial || EMPTY; setF(v); setHosts((v.host_patterns || []).join("\n")); } }, [open, initial]);
  const save = async () => {
    try {
      const body = { ...f, host_patterns: hosts.split(/[\s,]+/).filter(Boolean), max_per_line: Number(f.max_per_line), max_per_account: Number(f.max_per_account) };
      initial ? await api.put(`/admin/providers/${initial.id}`, body) : await api.post("/admin/providers", body);
      toast.success("Provider saved"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "bg-[#0B111B] border-slate-700";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="provider-form-dialog" className="bg-[#0F1722] border-slate-700">
        <DialogHeader><DialogTitle className="font-display">{initial ? "Edit provider" : "New provider"}</DialogTitle><DialogDescription className="text-xs text-slate-400">Host patterns and connection limits for an upstream provider.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="provider-name-input" className={inp} placeholder="Provider name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Textarea data-testid="provider-hosts-input" rows={3} className={`${inp} font-mono text-xs`} placeholder={"host patterns, one per line\n*.provider.tv\n161.97.86.237"} value={hosts} onChange={(e) => setHosts(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Max connections per line</span><Input data-testid="provider-per-line-input" type="number" min={1} className={`${inp} font-mono`} value={f.max_per_line} onChange={(e) => setF({ ...f, max_per_line: e.target.value })} /></label>
            <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Max connections per account</span><Input data-testid="provider-per-account-input" type="number" min={1} className={`${inp} font-mono`} value={f.max_per_account} onChange={(e) => setF({ ...f, max_per_account: e.target.value })} /></label>
          </div>
          <Input className={inp} placeholder="notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </div>
        <DialogFooter><Button data-testid="provider-save-btn" onClick={save} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Blocklist() {
  const [s, setS] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { api.get("/admin/settings").then(({ data }) => { setS(data); setText(data.blocklist.join("\n")); }); }, []);
  const save = async () => {
    try { const { data } = await api.put("/admin/settings", { ...s, blocklist: text.split(/[\s,]+/).filter(Boolean) }); setS(data); toast.success("Blocklist saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Never-pull blocklist" testid="blocklist-panel" right={<Button data-testid="blocklist-save-btn" size="sm" variant="ghost" onClick={save}>Save</Button>}>
      <p className="text-xs text-slate-400 mb-2">Hosts or URL fragments that may never be pulled. Enforced when a channel is saved and again at connect time (including after redirects).</p>
      <Textarea data-testid="blocklist-input" rows={8} className="bg-[#0B111B] border-slate-700 font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} />
    </Panel>
  );
}

export default function Providers() {
  const { data, reload } = usePoll("/admin/providers", 3000);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const del = async (id) => { await api.delete(`/admin/providers/${id}`); reload(); };
  return (
    <div>
      <PageHeader title="Providers & limits" sub="Upstream providers by host pattern. The origin never opens more connections than allowed — each line is pulled once."
        right={<Button data-testid="new-provider-btn" onClick={() => { setEdit(null); setOpen(true); }} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"><Plus size={15} /> Provider</Button>} />
      <div className="grid xl:grid-cols-[1.6fr_1fr] gap-4">
        <div className="space-y-3">
          {data && data.length === 0 && <Empty>No providers defined. Sources that match no provider are not limited.</Empty>}
          {(data || []).map((p) => {
            const pct = Math.min(100, (p.connections / p.max_per_account) * 100);
            return (
              <Panel key={p.id} testid={`provider-${p.id}`} title={p.name} right={<div className="flex gap-1">
                <button onClick={() => { setEdit(p); setOpen(true); }} className="p-1 text-slate-400 hover:text-slate-100"><Pencil size={13} /></button>
                <button data-testid={`provider-delete-${p.id}`} onClick={() => del(p.id)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 size={13} /></button></div>}>
                <div className="flex flex-wrap gap-1 mb-3">{p.host_patterns.map((h) => <Pill key={h} tone="cyan">{h}</Pill>)}</div>
                <div className="flex items-center gap-3 text-xs font-mono">
                  <span className="text-slate-400">account</span>
                  <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden"><div className={`h-full ${pct >= 100 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>
                  <span data-testid={`provider-usage-${p.id}`} className="text-slate-200">{p.connections}/{p.max_per_account}</span>
                  <span className="text-slate-500">per line ≤ {p.max_per_line}</span>
                </div>
                {Object.entries(p.lines_in_use || {}).map(([line, n]) => <div key={line} className="font-mono text-[11px] text-slate-500 truncate mt-1">{n}× {line}</div>)}
              </Panel>
            );
          })}
        </div>
        <Blocklist />
      </div>
      <ProviderForm open={open} onOpenChange={setOpen} initial={edit} onSaved={reload} />
    </div>
  );
}
